import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  InformationBoxComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { NameHelpers } from '@principle-theorem/principle-core';
import {
  ACC_DENTIST_PROVIDER_TYPE,
  AccPatientCommunicationType,
  AccPatientFormType,
  IAccClaimFormMetadata,
  IAccClaimProviderDetails,
  IAccPatientFormTracking,
  IPractice,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  HISTORY_DATE_TIME_FORMAT,
  Timestamp,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccPatientReferenceFormGroup } from '../../form-groups/acc-patient-referance.formgroup';

interface IAccProviderOptionGroup {
  group: string;
  options: IAccClaimProviderDetails[];
}

export interface IAccSelectPatientForms {
  [AccPatientFormType.AccPatientDetailsForm]: boolean;
  [AccPatientFormType.AccAccidentDetailsForm]: boolean;
  [AccPatientFormType.AccEmploymentDetailsForm]: boolean;
}

type AccPatientFormStatusDisplayMap = Record<
  AccPatientFormType,
  IAccPatientFormStatus | undefined
>;

interface IAccPatientFormStatus {
  feedback: string;
  actionTime: Timestamp;
}

export interface ISendAccNotificationEvent {
  notificationType: AccPatientCommunicationType;
  selectedForms: IAccSelectPatientForms;
}

@Component({
  selector: 'pr-acc-patient-reference-form',
  imports: [
    NgCommonModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatTooltipModule,
    InformationBoxComponent,
  ],
  templateUrl: './acc-patient-reference-form.component.html',
  styleUrls: ['./acc-patient-reference-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccPatientReferenceFormComponent {
  private _org = inject(OrganisationService);

  form = input.required<AccPatientReferenceFormGroup>();
  metadata = input<IAccClaimFormMetadata | undefined>(undefined);
  disabled = input(false);
  testMode = input(false);
  sendingForms = input(false);
  sendPatientFormsChange = output<ISendAccNotificationEvent>();

  providerOptions = signal<IAccProviderOptionGroup[]>([]);
  patientMobileNumber = signal<string | undefined>(undefined);
  hasSelectedForms = signal(true);

  providerCtrl = new TypedFormControl<IAccClaimProviderDetails | undefined>(
    undefined,
    [Validators.required]
  );

  selectedForms = new TypedFormGroup<IAccSelectPatientForms>({
    [AccPatientFormType.AccPatientDetailsForm]: new TypedFormControl<boolean>(
      true
    ),
    [AccPatientFormType.AccAccidentDetailsForm]: new TypedFormControl<boolean>(
      true
    ),
    [AccPatientFormType.AccEmploymentDetailsForm]:
      new TypedFormControl<boolean>(true),
  });

  patientFormStatus = signal<AccPatientFormStatusDisplayMap>({
    [AccPatientFormType.AccPatientDetailsForm]: undefined,
    [AccPatientFormType.AccAccidentDetailsForm]: undefined,
    [AccPatientFormType.AccEmploymentDetailsForm]: undefined,
  });

  disableNotificationButton = computed(
    () =>
      !this.hasSelectedForms() ||
      this.disabled() ||
      this.sendingForms() ||
      this.testMode()
  );

  readonly accPatientForm = AccPatientFormType;
  readonly dateFormat = HISTORY_DATE_TIME_FORMAT;

  constructor() {
    toObservable(this.form)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((form) => {
        const provider = form.controls.provider.value;

        if (provider) {
          this.providerCtrl.setValue(provider, {
            emitEvent: false,
          });
        }
      });

    toObservable(this.disabled)
      .pipe(takeUntilDestroyed())
      .subscribe((disabled) => {
        if (disabled) {
          this.providerCtrl.disable({ emitEvent: false });
          this.selectedForms.disable({ emitEvent: false });
        } else {
          this.providerCtrl.enable({ emitEvent: false });
          this.selectedForms.enable({ emitEvent: false });
        }
      });

    combineLatest([
      toObservable(this.form).pipe(filterUndefined()),
      this.providerCtrl.valueChanges,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([form, value]) => form.controls.provider.setValue(value));

    this.selectedForms.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        const selected = Object.keys(value).some((key) => {
          return value[key as keyof IAccSelectPatientForms];
        });
        this.hasSelectedForms.set(selected);
      });

    toObservable(this.metadata)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((metadata) => {
        const patientForms = metadata.patientForms;
        if (!patientForms) {
          return;
        }
        this._patchSelectedForms(patientForms);
        this._updatePatientFormStatus(patientForms);
      });

    this._setCurrentStafferAsProvider();
    this._buildProviderOptions();
  }

  compareFn(
    optionA: IAccClaimProviderDetails,
    optionB: IAccClaimProviderDetails
  ): boolean {
    return optionA && optionB
      ? optionA.providerId === optionB.providerId
      : false;
  }

  sendAsEmail(): void {
    this.sendPatientFormsChange.emit({
      notificationType: AccPatientCommunicationType.Email,
      selectedForms: this.selectedForms.getRawValue(),
    });
  }

  sendAsSMS(): void {
    this.sendPatientFormsChange.emit({
      notificationType: AccPatientCommunicationType.SMS,
      selectedForms: this.selectedForms.getRawValue(),
    });
  }

  private _patchSelectedForms(
    forms: Record<AccPatientFormType, IAccPatientFormTracking>
  ): void {
    Object.keys(this.selectedForms.controls).forEach((key) => {
      const formKey = key as keyof IAccSelectPatientForms;
      const patientForm = forms[formKey];
      const control = this.selectedForms.controls[formKey];
      if (!patientForm) {
        return;
      }
      return patientForm.selected
        ? control.setValue(true, { emitEvent: false })
        : control.setValue(false, { emitEvent: false });
    });
  }

  private _updatePatientFormStatus(
    forms: Record<AccPatientFormType, IAccPatientFormTracking>
  ): void {
    const updatedStatusDisplay = Object.entries(forms).reduce(
      (acc, [formType, tracking]) => {
        acc[formType as AccPatientFormType] = this._getTrackingStatus(tracking);
        return acc;
      },
      {} as AccPatientFormStatusDisplayMap
    );

    this.patientFormStatus.update((state) => ({
      ...state,
      ...updatedStatusDisplay,
    }));
  }

  private _getTrackingStatus(
    tracking: IAccPatientFormTracking
  ): IAccPatientFormStatus | undefined {
    if (tracking.submittedAt) {
      return {
        feedback: 'Submitted by patient',
        actionTime: tracking.submittedAt,
      };
    }
    if (tracking.issuedAt) {
      return {
        feedback: 'Sent to patient',
        actionTime: tracking.issuedAt,
      };
    }
    return undefined;
  }

  private _setCurrentStafferAsProvider(): void {
    this._org.staffer$
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((staffer) => {
        const providerId =
          staffer.metadata?.nz?.providerDetails?.acc?.providerId;

        if (!providerId) {
          return;
        }

        const providerName = NameHelpers.withoutTitle(staffer.user.name);

        this.providerCtrl.setValue({
          providerId,
          providerTypeCode:
            staffer.metadata?.nz?.providerDetails?.acc?.providerTypeCode ??
            ACC_DENTIST_PROVIDER_TYPE,
          firstName: NameHelpers.firstName(providerName),
          surname: NameHelpers.lastName(providerName),
        });
      });
  }

  private _buildProviderOptions(): void {
    combineLatest([
      this._org.practice$.pipe(filterUndefined()),
      this._org.practicePractitioners$,
    ])
      .pipe(
        takeUntilDestroyed(),
        map(([practice, practitioners]) => {
          const practiceGroup = this._practiceOption(practice);
          const practitionerGroup = this._practitionerOptions(practitioners);
          return compact([practiceGroup, practitionerGroup]);
        })
      )
      .subscribe((options) => this.providerOptions.set(options));
  }

  private _practiceOption(
    practice: WithRef<IPractice>
  ): IAccProviderOptionGroup | undefined {
    if (!practice.accSettings?.providerId) {
      return undefined;
    }
    return {
      group: 'Practice',
      options: [
        {
          providerId: practice.accSettings.providerId,
          providerTypeCode: ACC_DENTIST_PROVIDER_TYPE,
          firstName: NameHelpers.firstName(practice.name),
          surname: NameHelpers.lastName(practice.name),
        },
      ],
    };
  }

  private _practitionerOptions(
    practitioners: WithRef<IStaffer>[]
  ): IAccProviderOptionGroup | undefined {
    const providerPractitioners = practitioners.filter(
      (practitioner) =>
        !!practitioner.metadata?.nz?.providerDetails?.acc?.providerId
    );

    if (providerPractitioners.length === 0) {
      return undefined;
    }

    return {
      group: 'Practitioners',
      options: providerPractitioners.map((provider) => {
        const providerName = NameHelpers.withoutTitle(provider.user.name);
        return {
          providerId:
            provider.metadata?.nz?.providerDetails?.acc?.providerId ?? '',
          providerTypeCode:
            provider.metadata?.nz?.providerDetails?.acc?.providerTypeCode ??
            ACC_DENTIST_PROVIDER_TYPE,
          firstName: NameHelpers.firstName(providerName),
          surname: NameHelpers.lastName(providerName),
        };
      }),
    };
  }
}
